import React from 'react';
import { useBranchComparisonQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { BranchComparison } from '@/api/models';
import { useSales } from '@/pages/sales/SalesProvider';

/**
 * Returns a `BranchComparison` snapshot for the widget, scoped to the
 * page-level date range published through `SalesProvider`. Changing
 * the period filter on the page re-runs the underlying query because
 * `useBranchComparisonQuery` keys on `from` / `to`.
 *
 * Mapping rules mirror the other Sales hooks.
 */
export const useBranchComparison = () => {
    const { dateRange } = useSales();
    const query = useBranchComparisonQuery({
        from: dateRange.start,
        to: dateRange.end,
    });
    const { refetch } = query;

    const data = React.useMemo<BranchComparison | null>(
        () => (query.data?.success ? query.data.response : null),
        [query.data],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    return {
        data,
        error,
        isLoading: query.isPending,
        onRetry,
    } as const;
};
